import {
  GetAllSpacesResponse,
  MoveSectionCardResponse,
  ObjectType,
  RelatedSpace,
  Space,
  UpdateSectionCardResponse,
  UserAccess,
} from '@fullstory/proto/fs/services/dx-objects';
import { SpaceActions, SpaceRequestActions } from './actions';
import { combineReducers, createRequestReducer, RequestActionType } from '@fullstory/redux-util';
import { isOwnedObjectType, OwnedObjectType, SpaceRequestTypes, SpaceStateTypes } from './types';
import { AppBackendActions, AppBackendRequestTypes } from '../appbackend';
import { isNonNull } from '@fullstory/typeutils';

type SpaceState = { [id: string]: Space };
type SpaceAccessState = { [id: string]: UserAccess };

export const spaceAccess = (
  state: SpaceAccessState = {},
  action: RequestActionType<typeof SpaceRequestActions>,
): SpaceAccessState => {
  switch (action.type) {
    case SpaceRequestTypes.GetSpaceSuccess:
      return action.data.space?.id && action.data.userAccess
        ? { ...state, [action.data.space.id]: action.data.userAccess }
        : state;
  }

  return state;
};

export const spaces = (
  state: SpaceState = {},
  action: RequestActionType<typeof SpaceRequestActions>,
): SpaceState => {
  switch (action.type) {
    case SpaceRequestTypes.GetAllSpacesSuccess: {
      return action.data.spaces.reduce(
        (acc, val) => {
          const id = val.id;

          if (id != null) acc[id] = val;

          return acc;
        },
        { ...state },
      );
    }
    case SpaceRequestTypes.GetPersonalizedSidebarSuccess: {
      const responseState: SpaceState = {};
      for (const space of action.data.visibleSpaces) {
        responseState[space.id] = space;
      }

      for (const space of action.data.hiddenSpaces) {
        responseState[space.id] = space;
      }

      return { ...state, ...responseState };
    }
    case SpaceRequestTypes.DeleteSpaceRequest: {
      if (action.body?.id) {
        const spaces = { ...state };
        delete spaces[action.body.id];
        return spaces;
      }

      return state;
    }

    case SpaceRequestTypes.GetLibraryViewSuccess: {
      const responseState: SpaceState = {};
      for (const object of action.data.objects) {
        const space = object.owningSpace;
        if (space) {
          responseState[space.id] = space;
        }
      }

      return { ...state, ...responseState };
    }

    case SpaceRequestTypes.GetSpaceCardViewSuccess: {
      const responseState: SpaceState = {};
      const allObjects = action.data.sections
        .flatMap(s => s.cards.flatMap(c => c.object))
        .filter(isNonNull);
      for (const object of allObjects) {
        const space = object.owningSpace;
        if (space) {
          responseState[space.id] = space;
        }
      }

      return { ...state, ...responseState };
    }

    case SpaceRequestTypes.GetSpaceSuccess:
    case SpaceRequestTypes.CreateSpaceSuccess:
    case SpaceRequestTypes.UpdateSpaceSuccess: {
      return action.data.space?.id
        ? { ...state, [action.data.space.id]: action.data.space }
        : state;
    }

    case SpaceRequestTypes.GetDefaultSpaceSuccess: {
      return action.data.defaultSpace?.id
        ? { ...state, [action.data.defaultSpace.id]: action.data.defaultSpace }
        : state;
    }

    case SpaceRequestTypes.GetObjectSpaceSuccess: {
      const refs: SpaceState = {};
      for (const ref of action.data.referencedIn) {
        refs[ref.id] = ref;
      }
      return action.data.space?.id
        ? { ...state, ...refs, [action.data.space.id]: action.data.space }
        : { ...state };
    }
  }

  return state;
};

const allSpaces = createRequestReducer<GetAllSpacesResponse>()(
  SpaceRequestTypes.GetAllSpacesRequest,
  SpaceRequestTypes.GetAllSpacesSuccess,
  SpaceRequestTypes.GetAllSpacesFailure,
);

type SpaceOwnershipState = {
  [key in OwnedObjectType]: { [key: string]: { space: string; referencedIn: string[] } };
};

const addOwnership = (
  type: OwnedObjectType,
  objectId: string,
  spaceId: string,
  currentState: SpaceOwnershipState,
  referencedIn?: string[],
) => {
  // if we don't have refs here, use the existing state's references
  const currentRefs = !referencedIn ? currentState[type]?.[objectId]?.referencedIn ?? [] : [];
  const refs = [...new Set([...currentRefs, ...(referencedIn ?? [])])];
  return {
    ...currentState,
    [type]: {
      ...currentState[type],
      [objectId]: { space: spaceId, referencedIn: refs },
    },
  };
};

const addRelatedSpacesOwnership = (
  relatedSpaces: RelatedSpace[],
  currentState: SpaceOwnershipState,
) => {
  let updatedState = { ...currentState };
  for (const space of relatedSpaces) {
    for (const object of space.objects) {
      if (isOwnedObjectType(object.type) && object.id && space.space?.id) {
        updatedState = addOwnership(object.type, object.id, space.space.id, updatedState);
      }
    }
  }

  return updatedState;
};

// TODO - updates to ownership should be reflected here
export const ownership = (
  state: SpaceOwnershipState = {
    [ObjectType.METRIC]: {},
    [ObjectType.FUNNEL]: {},
    [ObjectType.RETENTION]: {},
    [ObjectType.SEGMENT]: {},
    [ObjectType.DASHBOARD]: {},
    [ObjectType.JOURNEY]: {},
    [ObjectType.HEATMAP]: {},
    [ObjectType.DATA_TABLE]: {},
  },
  action: AppBackendActions | SpaceActions,
): SpaceOwnershipState => {
  switch (action.type) {
    case AppBackendRequestTypes.GetMetricDetailViewSuccess: {
      const ownedSpace = action.data.deps?.spaces && Object.values(action.data.deps?.spaces)[0];
      if (action.request.body?.metricId && ownedSpace) {
        return addOwnership(ObjectType.METRIC, action.request.body?.metricId, ownedSpace.id, state);
      }
      break;
    }
    case AppBackendRequestTypes.GetFunnelDetailViewSuccess: {
      const ownedSpace = action.data.deps?.spaces && Object.values(action.data.deps?.spaces)[0];
      if (action.request.body?.funnelId && ownedSpace) {
        return addOwnership(ObjectType.FUNNEL, action.request.body?.funnelId, ownedSpace.id, state);
      }
      break;
    }
    case AppBackendRequestTypes.GetRetentionMetricDetailViewSuccess: {
      const ownedSpace = action.data.deps?.spaces && Object.values(action.data.deps?.spaces)[0];
      if (action.request.body?.retentionMetricId && ownedSpace) {
        return addOwnership(
          ObjectType.RETENTION,
          action.request.body?.retentionMetricId,
          ownedSpace.id,
          state,
        );
      }
      break;
    }
    case AppBackendRequestTypes.GetSegmentDetailViewSuccess: {
      const ownedSpace = action.data.deps?.spaces && Object.values(action.data.deps?.spaces)[0];
      if (action.request.body?.segmentId && ownedSpace) {
        return addOwnership(
          ObjectType.SEGMENT,
          action.request.body?.segmentId,
          ownedSpace.id,
          state,
        );
      }
      break;
    }
    case AppBackendRequestTypes.GetDashboardDetailViewSuccess: {
      const ownedSpace = action.data.deps?.spaces && Object.values(action.data.deps?.spaces)[0];
      let updatedState = { ...state };
      let hasUpdate = false;
      if (action.request.body?.dashboardId && ownedSpace) {
        hasUpdate = true;
        updatedState = addOwnership(
          ObjectType.DASHBOARD,
          action.request.body?.dashboardId.toString(),
          ownedSpace.id,
          state,
        );
      }

      if (action.data?.deps?.relatedSpaces) {
        hasUpdate = true;
        updatedState = addRelatedSpacesOwnership(
          Object.values(action.data.deps.relatedSpaces),
          updatedState,
        );
      }

      return hasUpdate ? updatedState : state;
    }
    case AppBackendRequestTypes.GetJourneyDetailViewSuccess: {
      const ownedSpace = action.data.deps?.spaces && Object.values(action.data.deps?.spaces)[0];
      if (action.request.body?.journeyId && ownedSpace) {
        return addOwnership(
          ObjectType.JOURNEY,
          action.request.body?.journeyId,
          ownedSpace.id,
          state,
        );
      }
      break;
    }
    case AppBackendRequestTypes.GetHeatmapDetailViewSuccess: {
      const ownedSpace = action.data.deps?.spaces && Object.values(action.data.deps?.spaces)[0];
      if (action.request.body?.heatmapId && ownedSpace) {
        return addOwnership(
          ObjectType.HEATMAP,
          action.request.body?.heatmapId,
          ownedSpace.id,
          state,
        );
      }
      break;
    }
    case AppBackendRequestTypes.GetHomeViewSuccess: {
      if (action.data?.deps?.relatedSpaces) {
        return addRelatedSpacesOwnership(Object.values(action.data.deps.relatedSpaces), state);
      }
      break;
    }
    case SpaceRequestTypes.GetObjectSpaceSuccess: {
      const type = action.request.body?.dxObjectType;
      const id = action.request.body?.dxObjectId;
      const ownedSpace = action.data.space;
      const referencedIn = action.data.referencedIn.map(s => s.id);

      if (isOwnedObjectType(type) && id && ownedSpace) {
        return addOwnership(type, id, ownedSpace.id, state, referencedIn);
      }
      break;
    }
    case SpaceRequestTypes.GetLibraryViewSuccess: {
      let updatedSpace = { ...state };
      for (const object of action.data.objects) {
        if (isOwnedObjectType(object.dxObjectType) && object.owningSpace?.id) {
          const referencedIn = object.referencedIn.map(s => s.id);
          updatedSpace = addOwnership(
            object.dxObjectType,
            object.dxObjectId,
            object.owningSpace.id,
            updatedSpace,
            referencedIn,
          );
        }
      }

      return updatedSpace;
    }
    case SpaceRequestTypes.GetSpaceCardViewSuccess: {
      let updatedSpace = { ...state };
      const allObjects = action.data.sections
        .flatMap(s => s.cards.flatMap(c => c.object))
        .filter(isNonNull);
      for (const object of allObjects) {
        if (isOwnedObjectType(object.dxObjectType) && object.owningSpace?.id) {
          const referencedIn = object.referencedIn.map(s => s.id);
          updatedSpace = addOwnership(
            object.dxObjectType,
            object.dxObjectId,
            object.owningSpace.id,
            updatedSpace,
            referencedIn,
          );
        }
      }

      return updatedSpace;
    }
    case SpaceRequestTypes.MoveObjectSuccess: {
      const type = action.request.body?.dxObjectType;
      const id = action.request.body?.dxObjectId;
      const ownedSpace = action.data.object?.owningSpace;
      const referencedIn = action.data.object?.referencedIn.map(s => s.id);

      if (isOwnedObjectType(type) && id && ownedSpace) {
        return addOwnership(type, id, ownedSpace.id, state, referencedIn);
      }
      break;
    }
    case SpaceStateTypes.MoveObjectToSpace: {
      const { dxObjectType, dxObjectId, newSpace } = action.request;

      if (isOwnedObjectType(dxObjectType) && dxObjectId && newSpace) {
        return addOwnership(dxObjectType, dxObjectId, newSpace, state);
      }
      break;
    }
    case SpaceRequestTypes.MoveObjectsSuccess: {
      const req = action.request.body;
      const rsp = action.data;

      let newState = { ...state };

      if (req !== undefined) {
        rsp.objects.forEach(obj => {
          const object = obj.object;
          if (object !== undefined && object !== null) {
            const type = object.dxObjectType;
            const id = object.dxObjectId;
            const ownedSpace = object.owningSpace;
            const referencedIn = object.referencedIn.map(s => s.id);
            if (isOwnedObjectType(type) && id && ownedSpace) {
              newState = addOwnership(type, id, ownedSpace.id, newState, referencedIn);
            }
          }
        });
      }
      return newState;
    }

    default:
      break;
  }
  return state;
};

const lastMovedCard = createRequestReducer<MoveSectionCardResponse>()(
  SpaceRequestTypes.MoveCardRequest,
  SpaceRequestTypes.MoveCardSuccess,
  SpaceRequestTypes.MoveCardFailure,
);

const lastUpdatedCard = createRequestReducer<UpdateSectionCardResponse>()(
  SpaceRequestTypes.UpdateCardRequest,
  SpaceRequestTypes.UpdateCardSuccess,
  SpaceRequestTypes.UpdateCardFailure,
);

type SpacesReducerState = {
  allSpaces: ReturnType<typeof allSpaces>;
  spaces: ReturnType<typeof spaces>;
  spaceAccess: ReturnType<typeof spaceAccess>;
  ownership: ReturnType<typeof ownership>;
  lastMovedCard: ReturnType<typeof lastMovedCard>;
  lastUpdatedCard: ReturnType<typeof lastUpdatedCard>;
};

export const spacesReducer = combineReducers<SpacesReducerState>({
  allSpaces,
  spaces,
  spaceAccess,
  ownership,
  lastMovedCard,
  lastUpdatedCard,
});
